import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as maplibregl from "maplibre-gl";
import type { Map as MlMap, MapMouseEvent } from "maplibre-gl";
import {
  Map as ReactMap,
  type ErrorEvent,
  type MapEvent,
} from "@vis.gl/react-maplibre";
import { ensureMapWorker, MapGpuNotice } from "../../lib/createMap";
import { loseWebglContextAfterRemove } from "../../lib/webglRelease";
import "maplibre-gl/dist/maplibre-gl.css";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Settings2, SquareCheckBig } from "lucide-react";
import type { DashboardField, FieldCovariate } from "../../types/contract";
import {
  useFieldLayerList,
  useLayerData,
  useFieldCovariates,
} from "../../hooks/queries";
import { covariateLayerFromStack, stackCellData } from "../../lib/stackClient";
import {
  polygonAreaM2,
  polygonsOf,
  pointInPolygons,
} from "../../lib/samplingDesign";
import { fieldTotalPrice } from "../../lib/samplePricing";
import { useSamplingPricePerAcre } from "../../hooks/useSamplingPrice";
import { choroplethFillColor } from "../../lib/cogColor";
import { behaviorColor } from "../../lib/behaviorZones";
import { withZoneLabelPoints } from "../../lib/labelAnchor";
import { bboxOfGeoJSON, isFittableBbox } from "../../lib/geo";
import { useUiMode, showsAdvancedControls } from "../../stores/uiModeStore";
import {
  fmtCov,
  scaledReading,
  automapHeadline,
  previewReadingsAtDepth,
  type AutomapKind,
  type ReadingKey,
} from "../../lib/previewReadings";
import { useAccuracyData } from "../../hooks/useAccuracyData";
import { scoreAccuracy } from "../../lib/accuracyIndex";
import { geomCentroid } from "../../lib/cellCentroid";
import { AccuracyBadge } from "../../components/AccuracyBadge";
import { zoneNumbersIn, zoneMatchPairs } from "../../lib/zoneColors";
import { suggestZoneCores } from "../../lib/zoneCoreSuggest";
import { pickWidestUncorrectedModel } from "../../lib/previewUnderlay";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "../../lib/metres";

const ringCentroid = geomCentroid;

// The binding constructs MapLibre itself. Same worker chunk createMap registers.
ensureMapWorker();

// One object: a new identity makes the binding call setStyle and drop the preview layers.
const PREVIEW_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
  sources: {
    sat: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution: "© Esri",
    },
  },
  layers: [{ id: "sat", type: "raster", source: "sat" }],
};

function previewCenter(field: DashboardField): {
  longitude: number;
  latitude: number;
} {
  if (isFittableBbox(field.bbox)) {
    return {
      longitude: (field.bbox[0] + field.bbox[2]) / 2,
      latitude: (field.bbox[1] + field.bbox[3]) / 2,
    };
  }
  const center = field.centroid ?? [-98, 39];
  return { longitude: center[0], latitude: center[1] };
}

export interface PreviewSchool {
  id: "highres" | "behavior" | "grid" | "relative" | "fieldrel";
  title: string;
  compareTo?: string;
  description: ReactNode; // plain string (whitespace-pre-line) OR rich JSX (e.g. behavior's bolded letters)
  automapId: string; // the automap-* manifest layer this map overlays
  automapKind: AutomapKind;
  // a cell grid covariate to color UNDER the overlay (Classic Grid: the grid-sampled OM IDW map).
  // Genius Map and Genius Zones ignore this and pick the uncorrected P/K/pH/OM/CEC model whose
  // shorter side around its standard is the largest fraction of that standard. Undefined → no underlay.
  underlayResolve?: (covs: FieldCovariate[]) => FieldCovariate | undefined;
  readingKeys: ReadingKey[]; // method-specific hover readings (covariate keys)
  onDefault: (fc?: GeoJSON.FeatureCollection) => void; // click the map → Save & Order this default design
  /*
   *
   * OPTIONAL since the 3-way mode toggle: retest hides it on Behavior, and
   * Map-With-Existing-Results hides it on every school — an absent handler renders NO chip, the
   * same construction Easy Mode uses.
   */
  onCustomize?: (fc?: GeoJSON.FeatureCollection) => void;
  /* * Chip label. Genius Zones in Map With Existing says “Pick Core Points”; every other card stays “Customize”. */
  customizeLabel?: string;
  // High-Res: a "learn more" note + per-model buttons (OM/pH/P/K/CEC) that open that nutrient's
  // info.
  infoNote?: string;
  modelButtons?: { key: string; label: string; onClick: () => void }[];
  favorite?: boolean;
  /*
   *
   * THE PICKED LAYER — Order-Retest / Map-With-Existing: when the user has picked a points/zone
   * layer, the card shows THAT layer instead of the school's AutoMap — the presets act on the
   * picked layer, so the picture must be the picked layer.
   */
  overrideFc?: GeoJSON.FeatureCollection | null;
  caption?: string;
}

interface InfoLine {
  label: string;
  text: string;
}

export function PreviewMap({
  field,
  school,
}: {
  field: DashboardField;
  school: PreviewSchool;
}) {
  const uiMode = useUiMode((s) => s.mode);
  const pricePerAcre = useSamplingPricePerAcre();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const [gpuMissing, setGpuMissing] = useState(false);
  const [ready, setReady] = useState(false);
  // hover readout: headline (point/zone name), the ZONE AVERAGE of each reading (zone maps only)
  // and the hovered cell's "grid value" reading — two sections for zone maps.
  const [info, setInfo] = useState<{
    headline: string | null;
    zoneLines: InfoLine[] | null;
    gridLines: InfoLine[];
  } | null>(null);
  const [orient, setOrient] = useState<"N" | "W">("N"); // map orientation (item 1: West-up for tall fields)

  const fittedRef = useRef(false);

  const { data: listed } = useFieldLayerList(field.field_id);
  const manifest = listed?.document.layers;
  const { data: covs } = useFieldCovariates(field.field_id);
  const covById = new Map((covs ?? []).map((c) => [c.key, c]));
  // Genius Map and Genius Zones share one pick per field: the uncorrected model with the widest
  // two-sided range around its standard (P 15, K 110, pH 6.3, OM 3, CEC 15).
  const nutrientUnderlay = school.id === "highres" || school.id === "fieldrel";
  const covCatalogKey = (covs ?? []).map((c) => c.key).join(",");
  const { data: pickedUnderlay } = useQuery({
    queryKey: ["preview-nutrient-underlay", field.field_id, covCatalogKey],
    enabled: nutrientUnderlay && covCatalogKey.length > 0,
    staleTime: 5 * 60_000,
    queryFn: () => pickWidestUncorrectedModel(field.field_id, covs ?? []),
  });
  const underlayCov = nutrientUnderlay
    ? pickedUnderlay === undefined
      ? undefined
      : (pickedUnderlay ?? school.underlayResolve?.(covs ?? []))
    : school.underlayResolve?.(covs ?? []);
  const automapMeta = (manifest ?? []).find((l) => l.id === school.automapId);
  const automapQuery = useLayerData(
    field.field_id,
    automapMeta?.id ?? null,
    automapMeta?.content_version,
    Boolean(automapMeta),
  );
  const automap = automapQuery.data;
  // Genius Zones preview only. Cores are placed inside THESE zone polygons (6 under 8 acres
  // 8 at 8 acres and up). The AutoMap suggested-cores layer is the 2.5 ac sample design —
  // several of those land in each Genius Zone, which is why the photo looked overcrowded.
  const showCores = school.id === "fieldrel";

  // The outline is the metadata document's boundary. The AutoMap geometry is
  // that one layer's detail (inline, or a signed URL fetched here).
  const { data: fetchedAutomap } = useQuery({
    queryKey: [
      "preview-automap-fc",
      field.field_id,
      school.automapId,
      automap?.content_version,
      automap?.signed_url,
    ],
    enabled: Boolean(automap && !automap.inline && automap.signed_url),
    staleTime: Infinity,
    queryFn: async () =>
      fetch(automap!.signed_url as string)
        .then((r) => r.json() as Promise<GeoJSON.GeoJSON>)
        .catch(() => undefined),
  });
  const geo = useMemo(() => {
    if (!listed) return undefined;
    return {
      boundaryGeom: listed.document.boundary as GeoJSON.GeoJSON | undefined,
      automapFc: (automap?.inline ?? fetchedAutomap) as
        GeoJSON.GeoJSON | undefined,
    };
  }, [listed, automap, fetchedAutomap]);
  // the design this card shows: the PICKED layer when there is one, else the school's AutoMap
  const amFcShown: GeoJSON.FeatureCollection | undefined =
    school.overrideFc ??
    (geo?.automapFc as GeoJSON.FeatureCollection | undefined);
  const zoneCoreKey =
    showCores && amFcShown?.features?.length
      ? `${automap?.content_version ?? "picked"}:${amFcShown.features
          .map((f) => {
            const p = f.properties as {
              zone?: unknown;
              zone_number?: unknown;
              area_acres?: unknown;
            } | null;
            return `${p?.zone_number ?? p?.zone ?? ""}:${p?.area_acres ?? ""}`;
          })
          .join(",")}`
      : "";
  const { data: coresFc } = useQuery({
    queryKey: ["preview-genius-cores", field.field_id, zoneCoreKey],
    enabled: zoneCoreKey.length > 0,
    staleTime: Infinity,
    queryFn: async (): Promise<GeoJSON.FeatureCollection | null> => {
      if (!amFcShown?.features?.length) return null;
      const points = await suggestZoneCores(field.field_id, amFcShown);
      if (!points.length) return null;
      return {
        type: "FeatureCollection",
        features: points.map((p) => ({
          type: "Feature",
          properties: {},
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        })),
      };
    },
  });

  // cell readings + (optional) colored OM underlay, from the decoded stack (cached per version)
  const keys = school.readingKeys.map((r) => r.key);
  const { data: cellData, isError: cellError } = useQuery({
    queryKey: [
      "preview-cell",
      field.field_id,
      school.id,
      keys.join(","),
      underlayCov?.key ?? "",
    ],
    enabled: Boolean(field?.field_id), // load the cell grid even with no readings (still want the hover glow)
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // "cell readings unavailable" has at least four causes that look identical on screen: the
      // field has no covariate stack (404), the signed URL came back null, the blob fetch was
      // blocked cross-origin, or the decode failed. Naming it turns the next report into a fact
      // instead of a fourth round of guessing.
      const cell = await stackCellData(field.field_id, keys).catch(
        (e: unknown) => {
          const err = e as { message?: string; response?: { status?: number } };
          console.warn(
            `[preview] cell readings unavailable for field=${field.field_id} (${school.id}): ` +
              `${err?.response?.status ? `HTTP ${err.response.status} — ` : ""}${err?.message ?? String(e)}. ` +
              `The stack blob is fetched DIRECTLY from storage.googleapis.com, so a cross-origin block here is ` +
              `bucket CORS, not this app's origin config.`,
            e,
          );
          throw e;
        },
      );
      const readingsFc: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: cell.geoms.map((g, i) => ({
          type: "Feature",
          id: i,
          properties: Object.fromEntries(
            keys.map((k, j) => [k, cell.features[j][i]]),
          ),
          geometry: g as GeoJSON.Geometry,
        })),
      };
      const omLayer = underlayCov
        ? await covariateLayerFromStack(field.field_id, underlayCov)
        : null;
      return { readingsFc, omLayer };
    },
  });

  // ZONE AVERAGES (zone maps only): assign each cell to the zone whose polygon contains its centroid, then
  // average each reading over the zone's cells. Keyed by zone feature index (== the pv-am feature-state id).
  const isZoneMap =
    school.automapKind === "behavior" || school.automapKind === "relative";
  const keysKey = keys.join("\u0001");
  const keysRef = useRef(keys);
  const fieldRef = useRef(field);
  const schoolRef = useRef(school);
  const amFcShownRef = useRef(amFcShown);
  const covByIdRef = useRef(covById);
  const isZoneMapRef = useRef(isZoneMap);
  useLayoutEffect(() => {
    fieldRef.current = field;
    schoolRef.current = school;
    amFcShownRef.current = amFcShown;
    covByIdRef.current = covById;
    isZoneMapRef.current = isZoneMap;
  });
  // The memo below reads `keys` during render. A layout effect would still be
  // holding the previous array when that memo runs.
  keysRef.current = keys;
  const zoneAverages = useMemo(() => {
    const keys = keysRef.current;
    const fc = amFcShown;
    if (!isZoneMap || !cellData?.readingsFc || !fc?.features?.length)
      return null;
    const zoneRings = fc.features.map((z) => polygonsOf(z.geometry));
    const sum = fc.features.map(() => ({}) as Record<string, number>);
    const cnt = fc.features.map(() => ({}) as Record<string, number>);
    for (const hx of cellData.readingsFc.features) {
      const c = ringCentroid(hx.geometry);
      if (!c) continue;
      let zi = -1;
      for (let i = 0; i < zoneRings.length; i++) {
        if (pointInPolygons(c, zoneRings[i])) {
          zi = i;
          break;
        }
      }
      if (zi < 0) continue;
      for (const k of keys) {
        const v = hx.properties?.[k];
        if (typeof v === "number" && Number.isFinite(v)) {
          sum[zi][k] = (sum[zi][k] ?? 0) + v;
          cnt[zi][k] = (cnt[zi][k] ?? 0) + 1;
        }
      }
    }
    const out = new Map<number, Record<string, number | null>>();
    fc.features.forEach((_, i) => {
      const rec: Record<string, number | null> = {};
      for (const k of keys) rec[k] = cnt[i][k] ? sum[i][k] / cnt[i][k] : null;
      out.set(i, rec);
    });
    // keysKey is the reading-key identity. The keys array is new every render.
    void keysKey;
    return out;
  }, [cellData, amFcShown, isZoneMap, keysKey]);

  // field.bbox is often null/degenerate on live — center loosely now; the reconcile effect fits to the
  // actual boundary GEOMETRY once it's resolved. Frozen on the first render, same as the old constructor.
  const [initialView] = useState(() => previewCenter(field));

  const onLoad = useCallback((event: MapEvent) => {
    const map = event.target;
    mapRef.current = map;
    loseWebglContextAfterRemove(map);
    map.resize(); // the grid cell may have laid out AFTER construction — pick up the real size
    setReady(true);
  }, []);

  const onError = useCallback((event: ErrorEvent) => {
    if (event.error instanceof maplibregl.GPUInitializationError) {
      setGpuMissing(true);
      return;
    }
    console.error(event.error);
  }, []);

  // safety net for a 0-size container at construction (flex/grid layout race): resize on any size change
  useEffect(() => {
    const map = mapRef.current;
    const el = containerRef.current;
    if (!map || !ready || !el) return;
    const ro = new ResizeObserver(() => {
      try {
        map.resize();
      } catch {
        /* removed */
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [ready]);

  // --- add sources/layers + bind hover; boundary+automap show even if the stack is still loading ----
  useEffect(() => {
    const field = fieldRef.current;
    const school = schoolRef.current;
    const amFcShown = amFcShownRef.current;
    const covById = covByIdRef.current;
    const isZoneMap = isZoneMapRef.current;
    const map = mapRef.current;
    if (!map || !ready) return;

    // setData matters: the reading keys resolve only AFTER the covariate catalog loads, so the cell
    // data arrives on a LATER pass — without setData the source keeps the initial empty-props data
    // and hover shows no readings.
    const setSrc = (id: string, data: GeoJSON.GeoJSON) => {
      const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData(data);
      else map.addSource(id, { type: "geojson", data });
    };
    const layer = (def: maplibregl.LayerSpecification) => {
      if (!map.getLayer(def.id)) map.addLayer(def);
    };

    // Rotate to use the wide card well: a field longer N/S than E/W renders WEST-up (bearing -90)
    // else NORTH-up — the only two orientations.
    if (!fittedRef.current) {
      const bb =
        bboxOfGeoJSON(geo?.boundaryGeom ?? amFcShown) ??
        (isFittableBbox(field.bbox) ? field.bbox : null);
      if (isFittableBbox(bb)) {
        const midLat = (bb[1] + bb[3]) / 2;
        const widthM =
          (bb[2] - bb[0]) *
          METRES_PER_DEGREE_LNG *
          Math.cos((midLat * Math.PI) / 180);
        const heightM = (bb[3] - bb[1]) * METRES_PER_DEGREE_LAT;
        const westUp = heightM > widthM;
        try {
          map.fitBounds(
            [
              [bb[0], bb[1]],
              [bb[2], bb[3]],
            ],
            {
              padding: 24,
              duration: 0,
              maxZoom: 16,
              bearing: westUp ? -90 : 0,
            },
          );
          fittedRef.current = true;
          setOrient(westUp ? "W" : "N");
        } catch {
          /* bad bbox */
        }
      }
    }

    // 1. boundary outline — dark casing + white line so it reads over satellite AND a light basemap
    if (geo?.boundaryGeom) {
      setSrc("src-pv-boundary", geo.boundaryGeom);
      layer({
        id: "pv-boundary-casing",
        type: "line",
        source: "src-pv-boundary",
        paint: {
          "line-color": "#000000",
          "line-width": 3.5,
          "line-opacity": 0.55,
        },
      });
      layer({
        id: "pv-boundary",
        type: "line",
        source: "src-pv-boundary",
        paint: { "line-color": "#ffffff", "line-width": 1.8 },
      });
    }

    // 2. colored nutrient cell grid underlay + 3. the transparent readings cell grid for hover —
    // both come from the decoded stack, so only once it's loaded (the AutoMap + boundary don't wait)
    if (cellData?.omLayer) {
      setSrc("src-pv-om", cellData.omLayer.inline as GeoJSON.GeoJSON);
      layer({
        id: "pv-om",
        type: "fill",
        source: "src-pv-om",
        paint: {
          "fill-color": choroplethFillColor(
            cellData.omLayer,
          ) as unknown as maplibregl.ExpressionSpecification,
          "fill-opacity": 0.8,
        },
      });
    }
    if (cellData?.readingsFc) {
      setSrc("src-pv-cell", cellData.readingsFc);
      layer({
        id: "pv-cell",
        type: "fill",
        source: "src-pv-cell",
        paint: { "fill-color": "#000000", "fill-opacity": 0 },
      });
      layer({
        id: "pv-cell-glow",
        type: "line",
        source: "src-pv-cell",
        paint: {
          "line-color": "#ffffff",
          "line-width": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            2.5,
            0,
          ],
        },
      });
    }

    // 4. the automap overlay (points / behavior zones / relative zones) — ids added for feature-state glow
    if (amFcShown && amFcShown.type === "FeatureCollection") {
      const raw = amFcShown;
      const withIds: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: (raw.features ?? []).map((f, i) => ({ ...f, id: i })),
      };
      // zone labels render from pole-of-inaccessibility POINTS — never on a zone edge fill/line
      // filter to polygons, the label symbol to points. Points only exist for zone kinds.
      setSrc(
        "src-pv-am",
        school.automapKind === "points"
          ? withIds
          : withZoneLabelPoints(withIds),
      );
      if (school.automapKind === "points") {
        layer({
          id: "pv-am",
          type: "circle",
          source: "src-pv-am",
          paint: {
            "circle-radius": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              8,
              5,
            ],
            "circle-color": "#E09F3E",
            "circle-stroke-color": "#fff",
            "circle-stroke-width": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              3,
              1.5,
            ],
          },
        });
        layer({
          id: "pv-am-label",
          type: "symbol",
          source: "src-pv-am",
          layout: {
            "text-field": ["to-string", ["coalesce", ["get", "num"], ""]],
            "text-font": ["Open Sans Regular"],
            "text-size": 11,
            "text-anchor": "bottom",
            "text-offset": [0, -0.7],
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#1f2937",
            "text-halo-color": "#fff",
            "text-halo-width": 1.4,
          },
        });
      } else if (school.id === "fieldrel") {
        // Genius Zones: the nutrient grid is the color. Zones are boundaries only. The fill is a
        // 1% veil so a hover still hits the polygon interior (a fully transparent fill is not pickable).
        layer({
          id: "pv-am",
          type: "fill",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: { "fill-color": "#ffffff", "fill-opacity": 0.01 },
        });
        layer({
          id: "pv-am-casing",
          type: "line",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "line-color": "#111827",
            "line-width": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              5,
              3.5,
            ],
            "line-opacity": 0.55,
          },
        });
        layer({
          id: "pv-am-line",
          type: "line",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "line-color": "#ffffff",
            "line-width": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              3,
              1.75,
            ],
          },
        });
        const lbl = [
          "to-string",
          [
            "coalesce",
            ["get", "zone_name"],
            ["get", "zone_number"],
            ["get", "zone"],
            "",
          ],
        ];
        layer({
          id: "pv-am-label",
          type: "symbol",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Point"],
          layout: {
            "text-field": lbl as unknown as maplibregl.ExpressionSpecification,
            "text-font": ["Open Sans Regular"],
            "text-size": 12,
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#1f2937",
            "text-halo-color": "#fff",
            "text-halo-width": 1.6,
          },
        });
      } else {
        const coded =
          school.automapKind === "behavior" &&
          withIds.features.some((f) => f.properties?.code != null);
        const fill = coded
          ? (() => {
              const present = zoneNumbersIn(withIds);
              return present.length
                ? [
                    "match",
                    ["to-number", ["coalesce", ["get", "zone"], -1]],
                    ...present.flatMap((z) => [z, behaviorColor(z)]),
                    "#888",
                  ]
                : "#888";
            })()
          : // relative zones: the SHARED palette sized to the zones actually present
            // (lib/zoneColors).
            (() => {
              const pairs = zoneMatchPairs(zoneNumbersIn(withIds));
              return pairs.length
                ? [
                    "match",
                    ["coalesce", ["get", "zone_number"], ["get", "zone"], -1],
                    ...pairs,
                    "#9ca3af",
                  ]
                : "#9ca3af";
            })();
        layer({
          id: "pv-am",
          type: "fill",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "fill-color": fill as unknown as maplibregl.ExpressionSpecification,
            "fill-opacity": 0.5,
          },
        });
        layer({
          id: "pv-am-line",
          type: "line",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "line-color": "#fff",
            "line-width": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              3,
              1,
            ],
          },
        });
        const lbl = coded
          ? ["coalesce", ["get", "code"], ""]
          : [
              "to-string",
              [
                "coalesce",
                ["get", "zone_name"],
                ["get", "zone_number"],
                ["get", "zone"],
                "",
              ],
            ];
        layer({
          id: "pv-am-label",
          type: "symbol",
          source: "src-pv-am",
          filter: ["==", ["geometry-type"], "Point"],
          layout: {
            "text-field": lbl as unknown as maplibregl.ExpressionSpecification,
            "text-font": ["Open Sans Regular"],
            "text-size": 12,
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#1f2937",
            "text-halo-color": "#fff",
            "text-halo-width": 1.6,
          },
        });
      }
      // keep the glow line above the zone fill so a hovered cell reads over the zones
      if (map.getLayer("pv-cell-glow")) map.moveLayer("pv-cell-glow");
      if (school.automapKind === "points" && map.getLayer("pv-am")) {
        map.moveLayer("pv-am");
        map.moveLayer("pv-am-label");
      }
      if (school.id === "fieldrel") {
        for (const id of [
          "pv-am-casing",
          "pv-am-line",
          "pv-am-label",
          "pv-boundary-casing",
          "pv-boundary",
        ]) {
          if (map.getLayer(id)) map.moveLayer(id);
        }
      }
    }

    // Suggested cores sit on the Genius Zones photo as tiny black dots, above the zone fill.
    if (showCores && coresFc?.features?.length) {
      setSrc("src-pv-cores", coresFc);
      if (map.getLayer("pv-cores-label")) map.removeLayer("pv-cores-label");
      if (map.getLayer("pv-cores")) {
        map.setPaintProperty("pv-cores", "circle-radius", 2);
        map.setPaintProperty("pv-cores", "circle-color", "#000000");
        map.setPaintProperty("pv-cores", "circle-stroke-width", 0);
      } else {
        layer({
          id: "pv-cores",
          type: "circle",
          source: "src-pv-cores",
          filter: ["==", ["geometry-type"], "Point"],
          paint: {
            "circle-radius": 2,
            "circle-color": "#000000",
            "circle-stroke-width": 0,
          },
        });
      }
      if (map.getLayer("pv-cores")) map.moveLayer("pv-cores");
      if (map.getLayer("pv-cell-glow")) map.moveLayer("pv-cell-glow");
    } else {
      if (map.getLayer("pv-cores-label")) map.removeLayer("pv-cores-label");
      if (map.getLayer("pv-cores")) map.removeLayer("pv-cores");
      if (map.getSource("src-pv-cores")) map.removeSource("src-pv-cores");
    }

    // 5. hover: highlight the hovered cell + automap feature; swap the info box to readings
    const cellHover = { current: null as number | null };
    const amHover = { current: null as number | null };
    const setHov = (
      source: string,
      ref: { current: number | null },
      id: number | null,
    ) => {
      if (ref.current === id) return;
      if (ref.current != null)
        map.setFeatureState({ source, id: ref.current }, { hover: false });
      if (id != null) map.setFeatureState({ source, id }, { hover: true });
      ref.current = id;
    };
    const idOf = (f?: maplibregl.MapGeoJSONFeature) =>
      typeof f?.id === "number" ? f.id : null;
    const probe = (e: MapMouseEvent) => {
      const hx = map.getLayer("pv-cell")
        ? map.queryRenderedFeatures(e.point, { layers: ["pv-cell"] })[0]
        : undefined;
      const am = map.getLayer("pv-am")
        ? map.queryRenderedFeatures(e.point, { layers: ["pv-am"] })[0]
        : undefined;
      setHov("src-pv-cell", cellHover, idOf(hx));
      if (map.getSource("src-pv-am")) setHov("src-pv-am", amHover, idOf(am));
      // "grid value" — the hovered cell's reading(s)
      const gridLines: InfoLine[] = [];
      const atDepth = previewReadingsAtDepth(
        school.readingKeys,
        covById,
        hx?.properties,
      );
      for (const r of school.readingKeys) {
        const raw = hx?.properties?.[r.key];
        const rawNum =
          typeof raw === "number" ? raw : raw != null ? Number(raw) : null;
        const solved = atDepth.get(r.key);
        const num = solved ?? rawNum;
        // solved != null → the value IS at the display depth; else the stored number, honestly labeled
        const text = fmtCov(
          covById.get(r.key),
          scaledReading(r, num),
          r.unitless,
          solved != null,
        ); // scaledReading → behavior drivers read 0–100 like the builder
        if (text != null) gridLines.push({ label: r.label, text });
      }
      // "zone average" — each reading averaged over the hovered zone (zone maps only)
      let zoneLines: InfoLine[] | null = null;
      const amId = idOf(am);
      if (isZoneMap && amId != null && zoneAverages) {
        const avgs = zoneAverages.get(amId);
        if (avgs) {
          zoneLines = [];
          // APPROXIMATION, stated rather than hidden: the zone's averaged inputs are solved once
          // so this is solve(mean), not mean(solve).
          const zoneAtDepth = previewReadingsAtDepth(
            school.readingKeys,
            covById,
            avgs as Record<string, unknown>,
          );
          for (const r of school.readingKeys) {
            const solved = zoneAtDepth.get(r.key);
            const v = solved ?? avgs[r.key] ?? null;
            const text = fmtCov(
              covById.get(r.key),
              scaledReading(r, v),
              r.unitless,
              solved != null,
            );
            if (text != null) zoneLines.push({ label: r.label, text });
          }
        }
      }
      const headline = automapHeadline(school.automapKind, am?.properties);
      setInfo(
        headline || zoneLines?.length || gridLines.length
          ? { headline, zoneLines, gridLines }
          : null,
      );
    };
    const clear = () => {
      setHov("src-pv-cell", cellHover, null);
      if (map.getSource("src-pv-am")) setHov("src-pv-am", amHover, null);
      setInfo(null);
    };
    map.on("mousemove", probe);
    map.on("mouseout", clear);
    // launch is handled by the wrapping <button> (a canvas click bubbles to it) — no map 'click' here
    // else onLaunch would fire twice
    map.getCanvas().style.cursor = "pointer";
    return () => {
      try {
        map.off("mousemove", probe);
        map.off("mouseout", clear);
      } catch {
        // The binding removes the map in its own effect, which runs first.
      }
    };
  }, [
    ready,
    cellData,
    geo,
    automap?.id,
    zoneAverages,
    school.overrideFc,
    showCores,
    coresFc,
  ]);

  const missing = Boolean(listed) && !automapMeta;
  const amFc = amFcShown;
  // live Soil Map Accuracy Index for THIS school's default design (top-right corner of the card)
  const { data: accData } = useAccuracyData(field.field_id);
  const accuracy = useMemo(() => {
    if (!accData || !amFc?.features?.length) return null;
    const kind =
      school.automapKind === "relative" || school.automapKind === "behavior"
        ? "zones"
        : school.automapId.includes("grid")
          ? "grid"
          : "ml";
    const points =
      kind === "zones"
        ? undefined
        : amFc.features
            .map((f) =>
              f.geometry.type === "Point"
                ? (f.geometry.coordinates as number[])
                : null,
            )
            .filter((c): c is number[] => Array.isArray(c) && c.length >= 2)
            .map((c) => ({ lng: c[0], lat: c[1] }));
    return scoreAccuracy({
      kind,
      cellLngLat: accData.cellLngLat,
      model: accData.model,
      points,
      zones: kind === "zones" ? amFc : undefined,
      areaAcres: accData.areaAcres,
    });
  }, [accData, amFc, school.automapId, school.automapKind]);
  const automapCount =
    amFc?.type === "FeatureCollection" ? (amFc.features?.length ?? 0) : 0;
  // field acres: prefer the dashboard value, fall back to the resolved boundary geometry (bbox/acres can be
  // null on live). The field total is this acreage times the quoted per-acre rate.
  const acresFromGeom = (() => {
    const polys = geo?.boundaryGeom ? polygonsOf(geo.boundaryGeom) : [];
    return polys.length
      ? polygonAreaM2(
          polys.length === 1
            ? { type: "Polygon", coordinates: polys[0] }
            : { type: "MultiPolygon", coordinates: polys },
        ) / 4046.86
      : 0;
  })();
  const acres = field.acres > 0 ? field.acres : acresFromGeom;
  const price = fieldTotalPrice(acres, pricePerAcre);
  const unitLabel =
    school.automapKind === "points"
      ? `${automapCount} sample point${automapCount === 1 ? "" : "s"}`
      : `${automapCount} zone${automapCount === 1 ? "" : "s"}`;
  const countLabel =
    price != null
      ? `${unitLabel} · $${Math.round(price).toLocaleString()}`
      : unitLabel;
  return (
    <div className="flex min-h-0 gap-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => school.onDefault(amFc)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            school.onDefault(amFc);
          }
        }}
        className="relative isolate flex min-h-0 min-w-0 flex-1 cursor-pointer flex-col overflow-hidden rounded-lg border border-line focus:outline-none focus:ring-2 focus:ring-accent"
        title={
          school.automapKind === "points"
            ? `Save & order the default ${school.title}`
            : `Save / order / download the default ${school.title}`
        }
      >
        <div className="absolute left-2 top-2 z-scrim rounded bg-raised/90 px-2 py-0.5 text-[11px] font-semibold text-fg shadow">
          {school.title}
        </div>
        {school.favorite && (
          <div
            className="absolute left-2 top-[26px] z-scrim flex items-center gap-1 rounded bg-sky-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow"
            title="Our Favorite — the mapping approach we recommend"
          >
            <SquareCheckBig size={11} /> Our Favorite
          </div>
        )}
        {/* orientation badge (item 1: N-up, or W-up when the field is longer N–S) */}
        {ready && (
          <div
            className="pointer-events-none absolute right-2 top-2 z-scrim rounded bg-raised/90 px-1.5 py-0.5 text-[10px] font-medium text-muted shadow"
            title={
              orient === "W"
                ? "West is up (field is longer N–S)"
                : "North is up"
            }
          >
            {orient} ↑
          </div>
        )}
        {ready && accuracy && (
          <div
            className="absolute right-2 top-[26px] z-scrim"
            onClick={(e) => e.stopPropagation()}
          >
            <AccuracyBadge
              result={accuracy}
              areaAcres={accData?.areaAcres}
              variant="corner"
            />
          </div>
        )}
        {gpuMissing ? (
          <MapGpuNotice className="min-h-0 min-w-0 flex-1" />
        ) : (
          <div ref={containerRef} className="min-h-0 min-w-0 flex-1">
            <ReactMap
              mapLib={maplibregl}
              mapStyle={PREVIEW_STYLE}
              initialViewState={{ ...initialView, zoom: 12 }}
              attributionControl={false}
              // MapLibre 6 defaults this to 4. Undefined keeps the previous overscale.
              zoomLevelsToOverscale={undefined}
              dragPan={false}
              scrollZoom={false}
              doubleClickZoom={false}
              boxZoom={false}
              dragRotate={false}
              touchZoomRotate={false}
              keyboard={false}
              onLoad={onLoad}
              onError={onError}
            />
          </div>
        )}
        {/* total samples/zones (item 5, lower-left) */}
        {ready && automapCount > 0 && (
          <div className="pointer-events-none absolute bottom-2 left-2 z-scrim rounded bg-raised/90 px-2 py-0.5 text-[10px] font-medium text-fg shadow">
            {countLabel}
          </div>
        )}
        {/* Hiding the entry point is the mechanism: the editors are unreachable once nothing opens them. */}
        {school.onCustomize && showsAdvancedControls(uiMode) && (
          <button
            className="absolute bottom-2 right-2 z-scrim flex items-center gap-1 rounded bg-accent px-2 py-1 text-[10px] font-medium text-canvas shadow hover:bg-accent-hover"
            onClick={(e) => {
              e.stopPropagation();
              school.onCustomize?.(amFc);
            }}
            title={
              school.customizeLabel ?? "Edit the points / zones before ordering"
            }
          >
            <Settings2 size={11} /> {school.customizeLabel ?? "Customize"}
          </button>
        )}
        {!ready && !missing && (
          <div className="pointer-events-none absolute inset-0 z-scrim flex items-center justify-center text-[11px] text-muted">
            <Loader2 size={14} className="mr-1 animate-spin" /> loading…
          </div>
        )}
        {missing && (
          <div className="pointer-events-none absolute inset-0 z-scrim flex items-center justify-center px-4 text-center text-[11px] text-muted">
            Preview not ready yet — it appears once the covariate build produces
            the AutoMaps.
          </div>
        )}
        {cellError && (
          <div className="pointer-events-none absolute left-2 top-8 z-scrim rounded bg-raised/90 px-2 py-0.5 text-[10px] text-amber-400 shadow">
            cell readings unavailable
          </div>
        )}
      </div>

      {/* the info box: description by default, method-specific readings on hover */}
      <div className="flex w-44 shrink-0 flex-col overflow-auto rounded-lg border border-line bg-raised/50 p-2 text-[11px]">
        {info ? (
          <>
            {info.headline && (
              <div className="mb-1 font-semibold text-fg">{info.headline}</div>
            )}
            {/* zone maps: two sections — the ZONE AVERAGE on top, the hovered cell's GRID VALUE below */}
            {info.zoneLines ? (
              <>
                <div className="text-[9px] font-semibold uppercase tracking-wide text-faint">
                  Zone average
                </div>
                {info.zoneLines.length > 0 ? (
                  info.zoneLines.map((l) => (
                    <div
                      key={l.label}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <span className="text-muted">{l.label}</span>
                      <span className="tnum font-medium text-fg">{l.text}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-faint">—</div>
                )}
                <div className="mt-1.5 text-[9px] font-semibold uppercase tracking-wide text-faint">
                  Grid value
                </div>
                {info.gridLines.length > 0 ? (
                  info.gridLines.map((l) => (
                    <div
                      key={l.label}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <span className="text-muted">{l.label}</span>
                      <span className="tnum font-medium text-fg">{l.text}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-faint">
                    edge/no-data cell — see zone average above
                  </div>
                )}
              </>
            ) : info.gridLines.length > 0 ? (
              info.gridLines.map((l) => (
                <div
                  key={l.label}
                  className="flex items-baseline justify-between gap-2"
                >
                  <span className="text-muted">{l.label}</span>
                  <span className="tnum font-medium text-fg">{l.text}</span>
                </div>
              ))
            ) : (
              <div className="text-faint">
                edge/no-data cell — no modeled value here
              </div>
            )}
          </>
        ) : (
          <>
            {school.compareTo && (
              <div className="mb-1 text-[10px] font-semibold text-accent">
                Compare to: {school.compareTo}
              </div>
            )}
            <div className="whitespace-pre-line text-muted">
              {school.description}
            </div>
            {school.infoNote && (
              <div className="mt-2 text-[10px] font-semibold text-fg">
                {school.infoNote}
              </div>
            )}
            {school.modelButtons && school.modelButtons.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {school.modelButtons.map((b) => (
                  <button
                    key={b.key}
                    className="rounded border border-line px-1.5 py-0.5 text-[10px] font-medium hover:bg-hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      b.onClick();
                    }}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
